/**
 * Festivos nacionales de Colombia.
 *
 * Basado en la Ley 51 de 1983 (Ley Emiliani) y, desde 2026,
 * la Ley 2578 de 2026 para Nuestra Señora del Rosario de Chiquinquirá.
 *
 * Se calcula por algoritmo en vez de mantener listas manuales por año,
 * para evitar que Nómina dependa de actualizar un calendario estático.
 */

const cache = new Map();

const pad2 = (value) => String(value).padStart(2, '0');

const utcDateToYmd = (date) =>
  `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;

const parseYmdUtc = (dateString) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateString || ''))) return null;
  const [year, month, day] = String(dateString).split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
};

const ymd = (year, month, day) => `${year}-${pad2(month)}-${pad2(day)}`;

const addDays = (dateString, days) => {
  const date = parseYmdUtc(dateString);
  if (!date) return '';
  date.setUTCDate(date.getUTCDate() + Number(days || 0));
  return utcDateToYmd(date);
};

/** Devuelve la misma fecha si ya es lunes; en otro caso, el lunes siguiente. */
const moveToMonday = (dateString) => {
  const date = parseYmdUtc(dateString);
  if (!date) return '';
  const day = date.getUTCDay(); // 0 domingo, 1 lunes ... 6 sábado
  const daysUntilMonday = day === 1 ? 0 : (8 - day) % 7;
  date.setUTCDate(date.getUTCDate() + daysUntilMonday);
  return utcDateToYmd(date);
};

/** Computus gregoriano (Meeus/Jones/Butcher). */
const getEasterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return ymd(year, month, day);
};

const holiday = (date, name, options = {}) => ({
  date,
  name,
  moved: !!options.moved,
  originalDate: options.originalDate || date,
  source: options.source || 'Ley 51 de 1983',
});

const movedHoliday = (originalDate, name, options = {}) => {
  const date = moveToMonday(originalDate);
  return holiday(date, name, {
    ...options,
    moved: date !== originalDate,
    originalDate,
  });
};

export const getColombiaHolidays = (yearValue) => {
  const year = Number(yearValue);
  if (!Number.isInteger(year) || year < 1900 || year > 2200) return [];
  if (cache.has(year)) return cache.get(year).map((item) => ({ ...item }));

  const easter = getEasterSunday(year);
  const rows = [
    holiday(ymd(year, 1, 1), 'Año Nuevo'),
    movedHoliday(ymd(year, 1, 6), 'Día de los Reyes Magos'),
    movedHoliday(ymd(year, 3, 19), 'Día de San José'),
    holiday(addDays(easter, -3), 'Jueves Santo'),
    holiday(addDays(easter, -2), 'Viernes Santo'),
    holiday(ymd(year, 5, 1), 'Día del Trabajo'),
    movedHoliday(addDays(easter, 39), 'Ascensión del Señor'),
    movedHoliday(addDays(easter, 60), 'Corpus Christi'),
    movedHoliday(addDays(easter, 68), 'Sagrado Corazón de Jesús'),
    movedHoliday(ymd(year, 6, 29), 'San Pedro y San Pablo'),
    holiday(ymd(year, 7, 20), 'Día de la Independencia'),
    holiday(ymd(year, 8, 7), 'Batalla de Boyacá'),
    movedHoliday(ymd(year, 8, 15), 'Asunción de la Virgen'),
    movedHoliday(ymd(year, 10, 12), 'Día de la Raza'),
    movedHoliday(ymd(year, 11, 1), 'Todos los Santos'),
    movedHoliday(ymd(year, 11, 11), 'Independencia de Cartagena'),
    holiday(ymd(year, 12, 8), 'Inmaculada Concepción'),
    holiday(ymd(year, 12, 25), 'Navidad'),
  ];

  // Ley 2578 de 2026: el 9 de julio es festivo nacional y se aplican
  // las reglas de traslado de la Ley 51 de 1983.
  if (year >= 2026) {
    rows.push(
      movedHoliday(ymd(year, 7, 9), 'Nuestra Señora del Rosario de Chiquinquirá', {
        source: 'Ley 2578 de 2026',
      })
    );
  }

  const deduped = new Map();
  rows.forEach((item) => {
    const previous = deduped.get(item.date);
    if (!previous) {
      deduped.set(item.date, item);
      return;
    }
    deduped.set(item.date, {
      ...previous,
      name: `${previous.name} / ${item.name}`,
      source: `${previous.source}; ${item.source}`,
    });
  });

  const result = Array.from(deduped.values()).sort((a, b) => a.date.localeCompare(b.date));
  cache.set(year, result);
  return result.map((item) => ({ ...item }));
};

export const getColombiaHoliday = (dateString) => {
  const date = parseYmdUtc(dateString);
  if (!date) return null;
  const year = date.getUTCFullYear();
  return getColombiaHolidays(year).find((item) => item.date === dateString) || null;
};

export const isColombiaHoliday = (dateString) => !!getColombiaHoliday(dateString);

export default {
  getColombiaHolidays,
  getColombiaHoliday,
  isColombiaHoliday,
};
